import { useCallback, useState } from 'react';
import { ConfirmPopup } from '~/commonComponents';
import WarningIcon from '~/images/icons/Warning';
import history from '../../../../../browserHistory';
import UserIcon from '../../../../../images/user-icon.svg';
import { Colors } from '../../../../../themes/colors';
import { RadioOption } from '../../../../registration/components/RadioOption';
import { getProfessionName } from '../../utils';
import { CollapsibleRow } from '../CollapsibleRow/CollapsibleRow';
import { Title } from '../CollapsibleRow/Title';
import { Value } from '../CollapsibleRow/Value';

/**
 * @deprecated Use InputJob.Professional from ~/commonComponents instead.
 * This component will be removed in a future version.
 */
export const ProfessionalRow = ({
  defaultValue,
  professionOptions,
  onToggle,
  isOpen,
  onConfirm,
  professionals,
}) => {
  const defaultInvitedProfessionals = history?.location?.state?.professionals;
  const professionTypesNames = history?.location?.state?.professionTypesNames;
  const [showProfessionalDoesntMatchPopup, setShowProfessionalDoesntMatchPopup] = useState(false);

  const [professionType, setProfessionType] = useState(
    defaultValue ?? professionOptions?.[0]?.code ?? 'RDH',
  );

  const isInvitedProfessionalMatching = useCallback(
    (newProfessionName) => {
      const hasOnlyOneProfessional = professionals?.length === 1;

      const hasInvitesFromOtherScreen = defaultInvitedProfessionals?.length > 0;

      const isProfessionalInvited =
        hasOnlyOneProfessional && defaultInvitedProfessionals?.includes(professionals?.[0]);

      const hasProfessionalProfessionTypes = professionTypesNames?.length > 0;
      const isNewProfessionIncludedInProfessionalProfessionTypes =
        professionTypesNames?.includes(newProfessionName);

      const conditionFails =
        hasOnlyOneProfessional &&
        hasInvitesFromOtherScreen &&
        isProfessionalInvited &&
        hasProfessionalProfessionTypes &&
        !isNewProfessionIncludedInProfessionalProfessionTypes;

      return !conditionFails;
    },
    [professionals, defaultInvitedProfessionals, professionTypesNames],
  );

  const handleSelectProfession = (code) => {
    const newProfessionName = getProfessionName(professionOptions, code);

    if (!isInvitedProfessionalMatching(newProfessionName)) {
      setShowProfessionalDoesntMatchPopup(true);
      return;
    }

    onConfirm({
      type: newProfessionName,
    });
    setProfessionType(code);
  };

  return (
    <>
      <CollapsibleRow
        dataCy="professional-row"
        isOpen={isOpen}
        onToggle={onToggle}
        TitleComponent={() => <Title text="Professional Type:" />}
        ValueComponent={() => (
          <Value
            customStyles={{
              color: Colors.neutral_600,
            }}
            text={getProfessionName(professionOptions, professionType)}
          />
        )}
        icon={UserIcon}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            width: '100%',
          }}
        >
          <div
            data-testid="profession-type-options"
            data-cy="profession-type-options"
            style={{
              marginRight: 68,
            }}
          >
            {professionOptions?.map(({ code, name }) => (
              <div style={{ marginBottom: 10 }} key={code}>
                <RadioOption
                  dataCy={`professional-option-${code.toLowerCase()}`}
                  onClick={() => handleSelectProfession(code)}
                  text={name}
                  checked={code === professionType}
                />
              </div>
            ))}
          </div>
        </div>
      </CollapsibleRow>

      {showProfessionalDoesntMatchPopup && (
        <ConfirmPopup
          dataCy="profession-mismatch-popup"
          dataTestId="profession-mismatch-popup"
          rightButtonDataCy="profession-mismatch-close"
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={74} height={74} color={Colors.warning_500} />
            </div>
          )}
          title="Professional Doesn’t Match Shift Criteria"
          description="The selected professional does not match the required license type. Please verify the profession type and candidate before proceeding."
          rightButtonText="Close"
          descriptionStyle={{ marginBottom: 32 }}
          rightButtonAction={() => setShowProfessionalDoesntMatchPopup(false)}
        />
      )}
    </>
  );
};
